{
  "page": {
    "title": "Audit log",
    "subtitle": "Review every significant change across Atlas OMS, filtered by module, actor, and time.",
    "coverage_note": "Coverage starts from when this ledger went live — earlier activity was not retroactively captured."
  },
  "error": {
    "title": "Couldn't load audit events",
    "body": "Something went wrong while loading the audit log. Try again.",
    "retry": "Retry"
  },
  "empty": {
    "title": "No audit events found",
    "body": "Try widening your filters or date range. Nothing shows for dates before this ledger went live, since earlier activity wasn't captured."
  },
  "column": {
    "time": "Time",
    "activity": "Activity",
    "module": "Module",
    "target": "Target",
    "actor": "Actor",
    "result": "Result",
    "view_details": "View details",
    "time_utc_suffix": "({{utc}} UTC)"
  },
  "filters": {
    "search_placeholder": "Search correlation ID, target, or actor…",
    "all_modules": "All modules",
    "all_results": "All results",
    "activity_placeholder": "Activity",
    "actor_placeholder": "Actor email",
    "attention": "Needs attention",
    "clear": "Clear filters",
    "date_24h": "Last 24 hours",
    "date_7d": "Last 7 days",
    "date_30d": "Last 30 days",
    "date_custom": "Custom range",
    "date_from": "From",
    "date_to": "To"
  },
  "group": {
    "pill": "{{count}} times since {{since}}",
    "pill_title": "This has happened {{count}} times since {{since}}. This row is the latest; the earlier ones are folded into it."
  },
  "timeline": {
    "banner_one": "Timeline for {{kind}} {{id}} · {{count}} event",
    "banner_other": "Timeline for {{kind}} {{id}} · {{count}} events",
    "clear": "Clear",
    "kind_order": "order",
    "kind_sku": "SKU"
  },
  "module": {
    "inventory": "Inventory",
    "orders": "Orders",
    "promotions": "Promotions",
    "users": "Users",
    "authentication": "Authentication",
    "integrations": "Integrations",
    "audit": "Audit"
  },
  "result": {
    "success": "Success",
    "partial": "Partial",
    "failed": "Failed"
  },
  "source": {
    "backoffice": "Backoffice",
    "csv": "CSV",
    "sino_api": "Sino API",
    "scheduler": "Scheduler",
    "system": "System"
  },
  "export": {
    "label_idle": "Export CSV",
    "label_starting": "Starting export…",
    "label_queued": "Queued for export…",
    "label_running": "Preparing your export…",
    "label_done": "Export CSV",
    "label_failed": "Export CSV",
    "aria_idle": "Export the filtered audit events as CSV",
    "aria_starting": "Starting export",
    "aria_queued": "Export queued",
    "aria_running": "Export running",
    "aria_done": "Export ready — start a new export",
    "aria_failed": "Export failed — try again",
    "download": "Download CSV ({{count}} rows)",
    "disabled_invalid_range": "Set both a start and end date before exporting",
    "toast_start_failed": "Couldn't start the export. Try again.",
    "toast_poll_failed": "Couldn't check the export status. Try again.",
    "toast_failed": "Export failed. Try again.",
    "toast_failed_reason": "Export failed: {{reason}}",
    "toast_done": "Export ready — {{count}} rows"
  },
  "drawer": {
    "title": "Event details",
    "error_body": "Something went wrong while loading this event. Try again.",
    "retry": "Retry",
    "subtitle": "{{module}} · {{time}}",
    "needs_attention": "Needs attention",
    "what_happened": "What happened",
    "to_fix": "To fix this",
    "details_title": "Details",
    "open_order": "Open order",
    "open_sku": "Open SKU in Inventory",
    "timeline_order": "Timeline for this order",
    "timeline_sku": "Timeline for this SKU",
    "occurrences": "{{count}} times, between {{first}} and {{last}}",
    "field_time": "Time",
    "field_time_utc": "UTC: {{utc}}",
    "field_actor": "Actor",
    "field_source": "Source",
    "field_target": "Target",
    "field_result": "Result",
    "field_occurrences": "Repeated",
    "field_correlation_id": "Correlation ID",
    "correlation_summary": "Correlation ID (for engineers)",
    "copy_correlation": "Copy correlation ID",
    "copy_correlation_success": "Correlation ID copied",
    "related_events": "Related events",
    "changes_title": "Changes",
    "changes_field": "Field",
    "changes_before": "Before",
    "changes_after": "After",
    "auth_title": "Authentication details",
    "auth_attempted_email": "Attempted email",
    "auth_ip_address": "IP address",
    "auth_user_agent": "User agent",
    "import_title": "Import summary",
    "import_succeeded": "Succeeded",
    "import_failed": "Failed",
    "import_skipped": "Skipped"
  },
  "detail": {
    "attempted_status": "Warehouse status",
    "cancel_reason": "Cancel reason",
    "changed": "Changed",
    "channel_state": "Marketplace status",
    "choice": "Resolution",
    "deleted": "Removed listings",
    "duplicate": "Duplicate listings",
    "failed_count": "Failed",
    "lifecycle": "Order status",
    "mapped": "Mapped listings",
    "marketplace": "Marketplace",
    "marketplace_error": "Marketplace error",
    "marketplace_result": "Marketplace result",
    "marketplace_status": "Marketplace status",
    "mode": "Push mode",
    "note": "Note",
    "order_id": "Order",
    "paused_reason": "Paused because",
    "paused_since": "Paused since",
    "qty": "Quantity",
    "qty_sent": "Quantity sent",
    "raw_status": "Marketplace raw status",
    "reason": "Reason",
    "result": "Result",
    "rows": "Listings affected",
    "rules_count": "Rules",
    "self_service": "Changed by the user",
    "sino_order_ref": "Warehouse reference",
    "sku": "SKU",
    "skipped_count": "Skipped",
    "succeeded_count": "Succeeded",
    "unmapped": "Unmapped listings",
    "warehouse_count": "Warehouses"
  },
  "activity": {
    "unknown": {
      "summary": "{{actor}} · {{target}}"
    },
    "authentication": {
      "login_failed": {
        "title": "Sign-in failed",
        "summary": "Sign-in for {{target}} failed ({{reason}}).",
        "brief": "{{reason}}"
      },
      "login_succeeded": {
        "title": "Signed in",
        "summary": "{{actor}} signed in.",
        "brief": "{{actor}}"
      }
    },
    "audit_log": {
      "exported": {
        "title": "Audit log exported",
        "summary": "{{actor}} exported the audit log.",
        "brief": "by {{actor}}"
      }
    },
    "inventory": {
      "bundle_reserve_adjusted": {
        "title": "Bundle reserve adjusted",
        "summary": "Order {{order_id}} adjusted the bundle reserve for SKU {{target}} ({{marketplace}}) from {{old}} to {{new}}.",
        "brief": "order {{order_id}} · {{marketplace}}"
      },
      "gwp_released": {
        "title": "Gift released",
        "summary": "Order {{order_id}} released {{delta}} gift unit(s) of SKU {{target}} on {{marketplace}}. Reserved went from {{old}} to {{new}}.",
        "brief": "−{{delta}} gift · order {{order_id}}"
      },
      "gwp_reserved": {
        "title": "Gift reserved",
        "summary": "Order {{order_id}} reserved {{delta}} gift unit(s) of SKU {{target}} on {{marketplace}}. Reserved went from {{old}} to {{new}}.",
        "brief": "+{{delta}} gift for order {{order_id}}"
      },
      "listing_multiplier_changed": {
        "title": "Listing multiplier changed",
        "summary": "{{actor}} changed the listing multiplier on {{target}} from {{old}} to {{new}}.",
        "brief": "{{old}} → {{new}}"
      },
      "on_hand_added": {
        "title": "Stock received",
        "summary": "{{delta}} units of SKU {{target}} arrived ({{marketplace}} On hand {{old}} → {{new}}).",
        "brief": "+{{delta}} · {{marketplace}} {{old}} → {{new}}"
      },
      "on_hand_deducted_ship": {
        "title": "Stock shipped out",
        "summary": "Order {{order_id}} shipped {{delta}} units of SKU {{target}} ({{marketplace}} On hand {{old}} → {{new}}).",
        "brief": "−{{delta}} for order {{order_id}} · {{old}} → {{new}}"
      },
      "on_hand_restocked": {
        "title": "Stock returned",
        "summary": "Order {{order_id}} returned {{delta}} units of SKU {{target}} ({{marketplace}} On hand {{old}} → {{new}}).",
        "brief": "+{{delta}} from order {{order_id}} · {{old}} → {{new}}"
      },
      "on_hand_set": {
        "title": "On hand updated",
        "summary": "Warehouse stock for SKU {{target}} ({{marketplace}}) changed from {{on_hand.old}} to {{on_hand.new}}.",
        "brief": "{{marketplace}} · {{on_hand.old}} → {{on_hand.new}}"
      },
      "reserve_added": {
        "title": "Stock reserved",
        "summary": "Order {{order_id}} reserved {{delta}} unit(s) of SKU {{target}} on {{marketplace}}. Reserved went from {{old}} to {{new}}.",
        "brief": "+{{delta}} for order {{order_id}} · {{marketplace}} {{old}} → {{new}}"
      },
      "reserve_cancelled": {
        "title": "Reservation cancelled",
        "summary": "Order {{order_id}} released {{delta}} unit(s) of SKU {{target}} on {{marketplace}} after it was cancelled.",
        "brief": "−{{delta}} · order {{order_id}} cancelled"
      },
      "reserve_released": {
        "title": "Reservation fulfilled",
        "summary": "Order {{order_id}} shipped: {{delta}} unit(s) of SKU {{target}} moved out of Reserved and On hand ({{marketplace}}).",
        "brief": "−{{delta}} · order {{order_id}} shipped"
      },
      "sku_created": {
        "title": "SKU created",
        "summary": "{{actor}} created SKU {{target}} ({{product_name.new}}).",
        "brief": "{{product_name.new}}"
      },
      "stock_buffer_changed": {
        "title": "Buffer changed",
        "summary": "{{actor}} changed the buffer on SKU {{target}} ({{marketplace}}) from {{old}} to {{new}}.",
        "brief": "{{marketplace}} · {{old}} → {{new}}"
      },
      "stock_lock_changed": {
        "title": "Lock changed",
        "summary": "{{actor}} changed the lock on SKU {{target}} ({{marketplace}}) from {{old}} to {{new}}.",
        "brief": "{{marketplace}} · {{old}} → {{new}}"
      },
      "stock_multiplier_changed": {
        "title": "Multiplier changed",
        "summary": "{{actor}} changed the multiplier on SKU {{target}} ({{marketplace}}) from {{old}} to {{new}}.",
        "brief": "{{marketplace}} · {{old}} → {{new}}"
      },
      "stock_recalculated": {
        "title": "Stock recalculated",
        "summary": "Sellable stock for SKU {{target}} ({{marketplace}}) was recalculated.",
        "brief": "{{marketplace}}"
      },
      "stock_reset": {
        "title": "Stock settings reset",
        "summary": "{{actor}} reset the lock, buffer and multiplier of SKU {{target}} ({{marketplace}}) to their defaults.",
        "brief": "{{marketplace}} · lock, buffer, multiplier back to default"
      }
    },
    "listing": {
      "discovery": {
        "title": "Listings scanned ({{target}})",
        "summary": "{{mapped}} mapped, {{unmapped}} unmapped, {{duplicate}} duplicates, {{deleted}} removed.",
        "brief": "{{mapped}} mapped · {{unmapped}} unmapped · {{duplicate}} dup"
      }
    },
    "marketplace_disconnect": {
      "title": "Marketplace disconnected",
      "summary": "{{actor}} disconnected {{target}}.",
      "brief": "by {{actor}}"
    },
    "order": {
      "cancel_partial": {
        "title": "Order partly cancelled",
        "summary": "Some lines of order {{target}} were cancelled on {{marketplace}} but others were refused. Refused lines stay reserved.",
        "fix": {
          "1": "Open the order to see which lines were refused.",
          "2": "Cancel the remaining lines on {{marketplace}} Seller Center, then refresh the order in Atlas."
        },
        "brief": "Refused lines stay reserved"
      },
      "cancel_refused": {
        "title": "Cancel refused by {{marketplace}}",
        "summary": "The cancel request for order {{target}} was refused by {{marketplace}} (reason given: {{cancel_reason}}). The order stays reserved.",
        "fix": {
          "1": "Open the order on {{marketplace}} Seller Center and check its status.",
          "2": "If it is already shipped, resolve the review flag as shipped.",
          "3": "Otherwise cancel it from {{marketplace}} Seller Center, then refresh the order in Atlas."
        },
        "brief": "Refused · reason given: {{cancel_reason}}"
      },
      "cancelled": {
        "title": "Order cancelled",
        "summary": "{{actor}} cancelled order {{target}} (reason: {{cancel_reason.new}}). Marketplace result: {{marketplace_result}}.",
        "brief": "{{cancel_reason.new}} · marketplace: {{marketplace_result}}"
      },
      "flag_cleared": {
        "title": "Review flag cleared",
        "summary": "The {{reason}} flag on order {{target}} was cleared.",
        "brief": "{{reason}} flag cleared"
      },
      "flagged": {
        "title": "Order needs review",
        "summary": "Order {{target}} was flagged {{reason}} ({{raw_status}}).",
        "oversold": {
          "summary": "Order {{target}} was flagged Oversold: {{marketplace}} sold more of SKU {{sku}} than is sellable.",
          "brief": "Oversold · SKU {{sku}} ({{marketplace}})"
        },
        "buyer_cancel_requested": {
          "summary": "The buyer asked to cancel order {{target}} on {{marketplace}}.",
          "brief": "Buyer asked to cancel ({{marketplace}})"
        },
        "cancelled_after_ship": {
          "summary": "Order {{target}} was cancelled on {{marketplace}} after it had shipped.",
          "brief": "Cancelled after shipping ({{marketplace}})"
        },
        "partial_cancel": {
          "summary": "Some lines of order {{target}} were cancelled on {{marketplace}}.",
          "brief": "Some lines cancelled ({{marketplace}})"
        },
        "brief": "{{reason}}"
      },
      "ingested": {
        "title": "Order received",
        "summary": "Order {{target}} arrived from the marketplace and was {{lifecycle.new}}.",
        "brief": "From marketplace · {{lifecycle.new}}"
      },
      "lifecycle_changed": {
        "title": "Order status changed",
        "summary": "Order {{target}} moved from {{old}} to {{new}} (marketplace status: {{channel_state}}).",
        "brief": "{{old}} → {{new}} ({{channel_state}})"
      },
      "line_cancelled": {
        "title": "Order line cancelled",
        "summary": "{{qty}} × SKU {{sku}} was cancelled on order {{target}} ({{marketplace}}).",
        "brief": "{{qty}} × SKU {{sku}} ({{marketplace}})"
      },
      "note": {
        "title": "Note added",
        "summary": "{{actor}} added a note to order {{target}}.",
        "brief": "by {{actor}}"
      },
      "note_deleted": {
        "title": "Note deleted",
        "summary": "{{actor}} deleted a note on order {{target}}.",
        "brief": "by {{actor}}"
      },
      "refreshed": {
        "title": "Order refreshed",
        "summary": "{{actor}} refreshed order {{target}} from the marketplace.",
        "changed": {
          "summary": "{{actor}} refreshed order {{target}} from the marketplace. Status changed to {{marketplace_status}}.",
          "brief": "Status now {{marketplace_status}}"
        },
        "unchanged": {
          "summary": "{{actor}} refreshed order {{target}} from the marketplace. Nothing changed.",
          "brief": "Nothing changed"
        },
        "brief": "From marketplace"
      },
      "review_resolved": {
        "title": "Review resolved",
        "summary": "{{actor}} resolved the {{reason}} flag on order {{target}} as {{choice}}.",
        "brief": "{{reason}} → {{choice}}"
      },
      "tracking_updated": {
        "title": "Tracking number added",
        "summary": "Order {{target}} got tracking number {{new}}.",
        "brief": "{{new}}"
      }
    },
    "promotions": {
      "_completed": {
        "title": "{{action}} completed",
        "summary": "{{succeeded_count}} succeeded, {{failed_count}} failed, {{skipped_count}} skipped.",
        "brief": "{{succeeded_count}} ok · {{failed_count}} failed · {{skipped_count}} skipped"
      },
      "campaign_created": {
        "title": "Campaign created",
        "summary": "{{actor}} created campaign {{name.new}}.",
        "brief": "{{name.new}}"
      },
      "promotion_cloned": {
        "title": "Promotion cloned",
        "summary": "{{actor}} cloned promotion {{target}} from {{cloned_from.new}}.",
        "brief": "from {{cloned_from.new}}"
      },
      "promotion_created": {
        "title": "Promotion created",
        "summary": "{{actor}} created promotion {{name.new}}.",
        "brief": "{{name.new}}"
      },
      "promotion_linked": {
        "title": "Promotion linked",
        "summary": "{{actor}} linked promotion {{target}} to group {{linked_group_id.new}}.",
        "brief": "group {{linked_group_id.new}}"
      },
      "promotion_rules_cloned": {
        "title": "Promotion rules cloned",
        "summary": "{{actor}} copied the rules of promotion {{target}}.",
        "brief": "by {{actor}}"
      },
      "promotion_rules_propagated": {
        "title": "Promotion rules propagated",
        "summary": "{{actor}} pushed the rules of promotion {{name.new}} out to its linked promotions.",
        "brief": "{{name.new}}"
      },
      "promotion_status_changed": {
        "title": "Promotion status changed",
        "summary": "{{actor}} changed promotion {{target}} from {{status.old}} to {{status.new}}.",
        "brief": "{{status.old}} → {{status.new}}"
      },
      "promotion_updated": {
        "title": "Promotion updated",
        "summary": "{{actor}} updated the rules of promotion {{target}}.",
        "brief": "by {{actor}}"
      }
    },
    "sino": {
      "status_update_blocked": {
        "title": "Warehouse update failed",
        "summary": "Atlas could not apply the warehouse status update for order {{order_id}}.",
        "stock_non_negative": {
          "title": "Shipment not recorded",
          "summary": "The warehouse shipped order {{order_id}}, but Atlas still shows it as {{lifecycle}}. Atlas could not deduct SKU {{sku}} ({{marketplace}}) because On hand is already 0 while units are still Reserved.\n\nThe warehouse will keep retrying until this is fixed.",
          "fix": {
            "1": "Count the physical stock of SKU {{sku}}.",
            "2": "If it was really shipped, set On hand to the real count in Inventory.",
            "3": "Resolve the Oversold flag on the order."
          },
          "brief": "SKU {{sku}} · On hand 0, still reserved"
        },
        "illegal_transition": {
          "title": "Warehouse status out of order",
          "summary": "The warehouse reported {{attempted_status}} for order {{order_id}}, but the order is {{lifecycle}} in Atlas and cannot move to that status.",
          "brief": "{{attempted_status}} while order is {{lifecycle}}"
        },
        "internal_error": {
          "title": "Warehouse update failed",
          "summary": "Atlas could not apply {{attempted_status}} from the warehouse for order {{order_id}}.",
          "fix": {
            "1": "Open the order and check its status.",
            "2": "If the warehouse status is right, ask an engineer to check the API log with the correlation ID below."
          },
          "brief": "{{attempted_status}} failed · see details"
        },
        "brief": "{{attempted_status}} from warehouse not applied"
      }
    },
    "stock": {
      "manual_sync": {
        "title": "Stock synced to {{marketplace}}",
        "summary": "{{actor}} pushed {{qty_sent}} units of SKU {{sku}} to {{marketplace}}. Result: {{result}}.",
        "brief": "{{qty_sent}} units of SKU {{sku}} · {{result}}"
      },
      "push_mode_changed": {
        "title": "Push mode changed ({{marketplace}})",
        "summary": "{{actor}} set {{marketplace}} stock push to {{mode}}.",
        "brief": "→ {{mode}}"
      },
      "push_paused": {
        "title": "Stock push paused ({{marketplace}})",
        "summary": "{{rows}} listings would have dropped to 0, so pushes were held to protect the shop. Reason: {{reason}}.",
        "fix": {
          "1": "Open Inventory and check why so many SKUs are at 0 (warehouse feed, locks, multipliers).",
          "2": "Once the numbers look right, resume pushing from the banner."
        },
        "brief": "{{rows}} listings would drop to 0"
      },
      "push_resumed": {
        "title": "Stock push resumed ({{marketplace}})",
        "summary": "{{actor}} resumed pushes to {{marketplace}} after {{paused_since}} (paused because: {{paused_reason}}).",
        "brief": "by {{actor}}"
      }
    },
    "user": {
      "created": {
        "title": "User created",
        "summary": "{{actor}} created {{target}} ({{email.new}}) with roles {{roles.new}}.",
        "brief": "{{email.new}} · {{roles.new}}"
      },
      "password_changed": {
        "title": "Password changed",
        "summary": "{{actor}} changed the password for {{target}}.",
        "brief": "by {{actor}}"
      },
      "password_reset": {
        "title": "Password reset by admin",
        "summary": "{{actor}} reset the password for {{target}}.",
        "brief": "by {{actor}}"
      }
    }
  },
  "value": {
    "reason": {
      "stock_non_negative": "Stock would go below zero",
      "illegal_transition": "Status out of order",
      "internal_error": "Internal error",
      "oversold": "Oversold",
      "buyer_cancel_requested": "Buyer asked to cancel",
      "cancelled_after_ship": "Cancelled after shipping",
      "partial_cancel": "Some lines cancelled",
      "sku_unmapped": "SKU not mapped",
      "money_parse_failed": "Amounts unreadable",
      "non_thb_currency": "Not in THB",
      "invalid_credentials": "Wrong email or password",
      "unauthorized": "Not allowed",
      "loop": "Scheduled push",
      "fast_path": "Pushed right after a change",
      "sync": "Manual sync",
      "sync_multi": "Manual sync (several SKUs)",
      "drift": "Drift correction",
      "resume": "Resumed after pause"
    },
    "result": {
      "ok": "Sent",
      "failed": "Failed",
      "shadow": "Shadow (not sent)",
      "no_listing": "No listing on the marketplace",
      "held_multi_warehouse": "Held: several warehouses",
      "held_breaker": "Held: push paused",
      "disabled": "Push turned off",
      "confirmed": "Confirmed",
      "partial": "Partly done",
      "refused": "Refused"
    },
    "mode": {
      "live": "Live",
      "shadow": "Shadow",
      "off": "Off"
    },
    "choice": {
      "accepted_on_seller_center": "Accepted on Seller Center",
      "cancelled_remaining_on_seller_center": "Cancelled the rest on Seller Center",
      "delivered_claim_filed": "Delivered, claim filed",
      "left_to_auto_decide": "Left for Atlas to decide",
      "other": "Other",
      "package_recalled": "Package recalled",
      "restocked": "Restocked"
    },
    "cancel_reason": {
      "buyer_request": "Buyer request",
      "other": "Other",
      "out_of_stock": "Out of stock"
    },
    "marketplace_result": {
      "confirmed": "Confirmed",
      "partial": "Partly cancelled",
      "refused": "Refused"
    },
    "lifecycle": {
      "received": "Received",
      "reserved": "Reserved",
      "picked": "Picked",
      "packed": "Packed",
      "shipped": "Shipped",
      "delivered": "Delivered",
      "returned": "Returned",
      "cancelled": "Cancelled"
    },
    "status": {
      "active": "Active",
      "draft": "Draft",
      "paused": "Paused",
      "finish": "Finished",
      "disconnected": "Disconnected"
    }
  },
  "field": {
    "on_hand": "On hand",
    "reserve": "Reserved",
    "bundle_reserve": "Bundle reserved",
    "lock": "Lock",
    "fake": "Buffer",
    "multiplier": "Multiplier",
    "tracking_no": "Tracking number",
    "is_pre": "Pre-order",
    "product_name": "Product name",
    "cloned_from": "Cloned from",
    "linked_group_id": "Linked group",
    "rules": "Rules",
    "rule_gift_limits": "Gift limits",
    "display_name": "Display name",
    "email": "Email",
    "roles": "Roles",
    "status": "Status",
    "lifecycle": "Order status",
    "cancel_reason": "Cancel reason",
    "name": "Name"
  },
  "target_type": {
    "order": "Order",
    "sku": "SKU",
    "stock_push": "Stock push",
    "marketplace_shop": "Marketplace shop",
    "marketplace_connection": "Marketplace connection",
    "promotion": "Promotion",
    "campaign": "Campaign",
    "promotion_bulk": "Promotions (bulk)",
    "user": "User",
    "sino_order_ref": "Warehouse reference"
  }
}
